  import React, { useState, useMemo, useEffect, useRef } from 'react';
  import { API_BASE_URL, AUTHORIZED_ROLES } from '../constants/property';
  import { useAuth } from '../contexts/AuthContext';
  import Grid from '../components/Grid';
  import Pagination from '../components/Pagination';
  import jsPDF from 'jspdf';
  import autoTable from 'jspdf-autotable';
  import * as XLSX from 'xlsx';
  import { Snackbar, Alert } from '@mui/material';
  import { formatIndianGstNumber, isValidIndianGstNumber } from '../utils/gst';
  import {
    parseRecordDateTime,
    compareRecordsNewestFirst,
    defaultSortDirectionForColumn,
  } from '../utils/tableSort';

  const ModalSection = ({ children }) => (
    <section className="mb-6 last:mb-0">
      {children}
    </section>
  );

  const AmountWithGstHint = ({ gstPercentage, withGST }) => {
    const pct = String(gstPercentage ?? '').trim().replace(/%$/, '').replace(/,/g, '');
    if (!pct || !Number.isFinite(parseFloat(pct)) || !withGST) return null;
    return (
      <p className="mt-1 text-xs text-gray-500">Amount with GST: {withGST}</p>
    );
  };

  const Transport = () => {
    const { hasAnyRole } = useAuth();
    const isAdministrator = hasAnyRole(AUTHORIZED_ROLES);
    
    // Helper function to get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
      const today = new Date(); 
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    /** Same as Vendors/StockIn: total × (1 + GST%/100) using gstPercentage. */
    const parseTransportGstPercent = (raw) => {
      const s = String(raw ?? '').trim().replace(/%$/, '').replace(/,/g, '');
      if (!s) return null;
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : null;
    };

    const computeTransportAmountWithGST = (totalAmountStr, gstPercentageStr) => {
      const a = parseFloat(String(totalAmountStr ?? '').replace(/[₹,]/g, ''));
      if (!Number.isFinite(a) || a <= 0) return '';
      const pct = parseTransportGstPercent(gstPercentageStr);
      if (pct == null) return '';
      return (a * (1 + pct / 100)).toFixed(2);
    };

    /** Balance = With GST − Advance when GST(%) is set; otherwise Total Amount − Advance. */
    const computeTransportBalance = (totalAmountStr, advanceStr, gstPercentageStr) => {
      const advance = parseFloat(String(advanceStr ?? '').replace(/[₹,]/g, '')) || 0;
      const total = parseFloat(String(totalAmountStr ?? '').replace(/[₹,]/g, '')) || 0;
      const withGST = computeTransportAmountWithGST(totalAmountStr, gstPercentageStr);
      const base = withGST !== '' ? parseFloat(withGST) : total;
      return Math.round((base - advance) * 100) / 100;
    };

    const isAdvanceFilled = (advanceStr) => {
      const s = String(advanceStr ?? '').trim();
      return s !== '' && s !== '-';
    };

    const resolveTransportBalance = (totalAmountStr, advanceStr, gstPercentageStr) => {
      if (!isAdvanceFilled(advanceStr)) return '';
      const total = parseFloat(String(totalAmountStr ?? '').replace(/[₹,]/g, ''));
      const advance = parseFloat(String(advanceStr ?? '').replace(/[₹,]/g, ''));
      if (!Number.isFinite(total) || !Number.isFinite(advance)) return '';
      const b = computeTransportBalance(totalAmountStr, advanceStr, gstPercentageStr);
      return Number.isFinite(b) ? String(b) : '';
    };

    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingTransportId, setEditingTransportId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // Report filters
    const [datePreset, setDatePreset] = useState(''); // today|yesterday|last7|last30|thisMonth|lastMonth|custom
    const [dateDisplay, setDateDisplay] = useState('');
    const [showDateDropdown, setShowDateDropdown] = useState(false);
    const [showCustomCalendar, setShowCustomCalendar] = useState(false);
    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedType, setSelectedType] = useState('all');
    const [selectedVehicleNo, setSelectedVehicleNo] = useState('all');
    const [selectedVehicleProvider, setSelectedVehicleProvider] = useState('all');
    const [selectedSupervisor, setSelectedSupervisor] = useState('all');
    const [selectedDCNumber, setSelectedDCNumber] = useState('all');
    const [typeInputValue, setTypeInputValue] = useState('');
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [vehicleProviderInputValue, setVehicleProviderInputValue] = useState('');
    const [showVehicleProviderDropdown, setShowVehicleProviderDropdown] = useState(false);
    const [supervisorInputValue, setSupervisorInputValue] = useState('');
    const [showSupervisorDropdown, setShowSupervisorDropdown] = useState(false);
    const [supervisorsFromApi, setSupervisorsFromApi] = useState([]);
    const [maintenanceSiteOptions, setMaintenanceSiteOptions] = useState(() => [
      { value: 'ST1998', label: 'ST1998' },
      { value: 'ST2765', label: 'ST2765' },
      { value: 'ST3865', label: 'ST3865' },
      { value: 'ST4765', label: 'ST4765' },
      { value: 'ST19098', label: 'ST19098' },
      { value: 'ST2003', label: 'ST2003' },
    ]);
    const [dcNumberInputValue, setDCNumberInputValue] = useState('');
    const [showDCNumberDropdown, setShowDCNumberDropdown] = useState(false);
    const [formData, setFormData] = useState({
      date: getTodayDate(),
      dcNumber: '',
      site: '',
      transporter: '',
      type: '',
      vehicleprovider: '',
      vechileNo: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      startreading: '',
      endreading: '',
      grossWeight: '',
      tareWeight: '',
      netWeight: '',
      noOfLoad: '',
      loadWt: '',
      hours: '',
      dayNight: '',
      totalAmount: '',
      withGST: '',
      invoiceNumber: '',
      gstNumber: '',
      gstPercentage: '',
      advance: '',
      balance: '',
      supervisor: '',
      numberOfDays: '',
      totalHours: ''
    });
    const [showFilters, setShowFilters] = useState(true);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [sortColumn, setSortColumn] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const defaultVisibleColumns = {
      date: true,
      dcNumber: true,
      sites: true,
      transporter: true,
      type: true,
      vehicleprovider: true,
      vechileNo: true,
      startTime: true,
      endTime: true,
      startDate: true,
      endDate: true,
      startreading: true,
      endreading: true,
      totalReading: true,
      noOfLoad: true,
      loadWt: false,
      totalHours: true,
      shiftType: true,
      totalAmount: true,
      invoiceNumber: true,
      gstNumber: true,
      gstPercentage: true,
      withGST: true,
      advance: true,
      balance: true,
      supervisor: true,
      numberOfDays: true,
      grossWeight: true,
      tareWeight: true,
      netWeight: true,
      actions: true
    };
    const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
    const [isLoading, setIsLoading] = useState(true);

    const columnMenuRef = useRef(null);

    // Fetch supervisors (role = Supervisor) from employee API
    useEffect(() => {
      const fetchSupervisors = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/employee`, {
            method: 'GET',
            mode: 'cors'
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();
          const options = (Array.isArray(data) ? data : [])
            .filter(emp => emp.role === 'Supervisor')
            .map(emp => {
              const label =
                emp.userName ||
                emp.employeeName ||
                `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
              return label ? { value: label, label } : null;
            })
            .filter(Boolean);

          setSupervisorsFromApi(options);
        } catch (err) {
          console.error('Error fetching supervisors from employee API:', err);
        }
      };

      fetchSupervisors();
    }, []);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/maintence/Site`, { method: 'GET', mode: 'cors' });
          if (!res.ok) return;
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'site');
          const seen = new Set();
          const opts = filtered
            .map((r) => {
              const label = String(r.value ?? r.code ?? '').trim();
              if (!label) return null;
              const key = label.toLowerCase().replace(/\s+/g, '');
              if (seen.has(key)) return null;
              seen.add(key);
              return { value: label, label };
            })
            .filter(Boolean)
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
          if (!cancelled && opts.length) setMaintenanceSiteOptions(opts);
        } catch {
          /* keep seeded site list */
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    // Column definitions for visibility toggle
    const columnDefinitions = [
      { key: 'date', label: 'Date', sortable: true },
      { key: 'dcNumber', label: 'DC Number', sortable: true },
      { key: 'site', label: 'Site', sortable: true },
      { key: 'transporter', label: 'Transporter', sortable: true },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'vehicleprovider', label: 'Vehicle Provider', sortable: true },
      { key: 'vechileNo', label: 'Vehicle Number', sortable: true },
      { key: 'startTime', label: 'Start Time', sortable: false },
      { key: 'endTime', label: 'End Time', sortable: false },
      { key: 'startDate', label: 'Start Date', sortable: true },
      { key: 'endDate', label: 'End Date', sortable: true },
      { key: 'startreading', label: 'Start Reading', sortable: false },
      { key: 'endreading', label: 'End Reading', sortable: false },
      { key: 'totalReading', label: 'Total Reading', sortable: false },
      { key: 'noOfLoad', label: 'Number of Loads', sortable: true },
      { key: 'numberOfDays', label: 'No. of Days', sortable: true },
      { key: 'grossWeight', label: 'Gross Weight', sortable: false },
      { key: 'tareWeight', label: 'Tare Weight', sortable: false },
      { key: 'netWeight', label: 'Net Weight', sortable: false },
      { key: 'totalHours', label: 'Total Hours', sortable: false },
      { key: 'shiftType', label: 'Shift Type', sortable: true },
      { key: 'invoiceNumber', label: 'Invoice Number', sortable: true },
      { key: 'gstNumber', label: 'GST Number', sortable: true },
      { key: 'totalAmount', label: 'Total Amount', sortable: true },
      { key: 'gstPercentage', label: 'GST(%)', sortable: true },
      { key: 'withGST', label: 'With GST', sortable: true },
      { key: 'advance', label: 'Advance', sortable: true },
      { key: 'balance', label: 'Balance', sortable: true },
      { key: 'supervisor', label: 'Supervisor', sortable: true },
      { key: 'actions', label: 'Actions', sortable: false }
    ];

    const [transportData, setTransportData] = useState([
      {
        id: "1",
        date: "Oct 01, 2025",
        dcNumber: "DC001234",
        transporter: "ABC Transport",
        type: "JCB",
        vehicleprovider: "TKD",
        startreading: "09:00 AM",
        endreading: "01:00 PM",
        totalHours: "04:00",
        totalAmount: "5000",
        advance: "2000",
        balance: "3000",
        supervisor: "Selva",
        lorryNumber: "-",
        vechileNo: "TN13 2345",
        noofdays: "1"
      },
      {
        id: "2",
        date: "Oct 02, 2025",
        dcNumber: "DC001235",
        transporter: "XYZ Logistics",
        type: "Poklaine",
        vehicleprovider: "Rent",
        startreading: "10:00 AM",
        endreading: "02:00 PM",
        totalHours: "04:00",
        totalAmount: "6000",
        advance: "2500",
        balance: "3500",
        supervisor: "Kumar",
        lorryNumber: "-",
        vechileNo: "TN13 2345",
        noofdays: "1"
      },
      {
        id: "3",
        date: "Oct 03, 2025",
        dcNumber: "DC001236",
        transporter: "DEF Transport",
        type: "Tipper",
        vehicleprovider: "TKD",
        noOfLoad: "5",
        loadWt: "2500",
        totalAmount: "4000",
        advance: "1500",
        balance: "2500",
        supervisor: "Raj",
        lorryNumber: "TN13 2345",
        vechileNo: "-",
        noofdays: "1"
      },
      {
        id: "4",
        date: "Oct 03, 2025",
        dcNumber: "DC0012310",
        transporter: "Fast Track Transport",
        type: "Bracker",
        vehicleprovider: "Rent",
        noOfLoad: "3",
        loadWt: "1800",
        totalAmount: "4500",
        advance: "1500",
        balance: "3000",
        supervisor: "Sekar",
        lorryNumber: "TN13 2345",
        vechileNo: "-",
        noofdays: "1"
      },
      {
        id: "5",
        date: "Oct 04, 2025",
        dcNumber: "DC001237",
        transporter: "Reliable Cargo",
        type: "JCB",
        vehicleprovider: "TKD",
        startreading: "12:00 PM",
        endreading: "04:00 PM",
        totalHours: "04:00",
        totalAmount: "5500",
        advance: "2000",
        balance: "3500",
        supervisor: "Kumar",
        lorryNumber: "-",
        vechileNo: "TN13 2345",
        noofdays: "1"
      },
      {
        id: "5",
        date: "Oct 05, 2025",
        dcNumber: "DC001238",
        transporter: "Speed Logistics",
        type: "Poklaine",
        vehicleprovider: "Rent",
        startreading: "12:30 PM",
        endreading: "03:00 PM",
        totalHours: "02:30",
        totalAmount: "3500",
        advance: "1000",
        balance: "2500",
        supervisor: "Jana",
        lorryNumber: "-",
        vechileNo: "TN13 2345",
        noofdays: "1"
      },
      {
        id: "6",
        date: "Oct 06, 2025",
        dcNumber: "DC001239",
        transporter: "Quick Delivery",
        type: "Tipper",
        vehicleprovider: "TKD",
        noOfLoad: "4",
        loadWt: "2200",
        totalAmount: "6500",
        advance: "3000",
        balance: "3500",
        supervisor: "Kathir",
        lorryNumber: "TN13 2345",
        vechileNo: "-",
        noofdays: "1"
      },
      {
        id: "7",
        date: "Oct 07, 2025",
        dcNumber: "DC001240",
        transporter: "Heavy Duty Transport",
        type: "Bracker",
        vehicleprovider: "Rent",
        startreading: "03:00 PM",
        endreading: "05:00 PM",
        totalHours: "02:00",
        totalAmount: "3000",
        advance: "1000",
        balance: "2000",
        supervisor: "Sathik",
        lorryNumber: "-",
        vechileNo: "TN13 2345",
        noofdays: "2"
      },
      {
        id: "8",
        date: "Oct 08, 2025",
        dcNumber: "DC001241",
        transporter: "Bulk Transport Co",
        type: "Poklaine",
        vehicleprovider: "TKD",
        startreading: "04:30 PM",
        endreading: "06:00 PM",
        totalHours: "01:30",
        totalAmount: "2500",
        advance: "500",
        balance: "2000",
        supervisor: "Saran",
        lorryNumber: "-",
        vechileNo: "TN13 2345",
        noofdays: "4"
      },
      {
        id: "9",
        date: "Oct 09, 2025",
        dcNumber: "DC001242",
        transporter: "Industrial Logistics",
        type: "Tipper",
        vehicleprovider: "Rent",
        startreading: "04:00 PM",
        endreading: "05:00 PM",
        totalHours: "01:00",
        totalAmount: "2000",
        advance: "500",
        balance: "1500",
        supervisor: "Vignesh",
        lorryNumber: "TN13 2345",
        vechileNo: "-",
        noofdays: "1"
      },
      {
        id: "10",
        date: "Oct 10, 2025",
        dcNumber: "DC001243",
        transporter: "Eco Transport",
        type: "Bracker",
        vehicleprovider: "TKD",
        startreading: "05:00 PM",
        endreading: "07:00 PM",
        totalHours: "02:00",
        totalAmount: "4000",
        advance: "1500",
        balance: "2500",
        supervisor: "Sudha",
        lorryNumber: "-",
        vechileNo: " TN13 2345",
        noofdays: "1"
      }
    ]);

    // Filter options
    const typeOptions = [
      { value: 'JCB', label: 'JCB' },
      { value: 'Poklaine', label: 'Poklaine' },
      { value: 'Tipper', label: 'Tipper' },
      { value: 'Bracker', label: 'Bracker' },
    ];

    // Helper function to transform API data to component structure
    const transformApiData = (apiData) => {
      const records = [];
      let recordId = 1;

      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}, ${date.getFullYear()}`;
      };

      let apiIndex = 0;

      apiData.forEach((item) => {
        // Convert timeIn/timeOut from "HH:MM:SS" to "HH:MM" format for display (API may send null)
        const convertTimeForDisplay = (timeStr) => {
          if (timeStr == null || timeStr === '' || timeStr === '-' || timeStr === 'null') return '-';
          if (typeof timeStr !== 'string') return '-';
          if (timeStr.match(/^\d{2}:\d{2}$/)) return timeStr;
          if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) return timeStr.substring(0, 5);
          return timeStr;
        };

        const safeStr = (val) => (val != null && val !== '') ? String(val) : '-';
        const safeNum = (val) => (val != null && val !== '') ? String(val) : '0';

        // Map API response keys to table record (transportName, transportId, date, dcNumber, vehicleType, vehicleProvider, transporterName, startingReading, endingReading, totalHours, totalAmount, advance, balance, supervisorName, timeIn, timeOut, totalReading, shiftType, startDate, endDate, numberOfDays, numberOfLoads, grossWeight, tareWeight, netWeight, sites)
        const record = {
          id: String(recordId++),
          apiIndex: apiIndex++,
          rawDate: item.date || '',
          rawStartDate: item.startDate || '',
          rawEndDate: item.endDate || '',
          transportId: item.transportId ?? item.id ?? recordId,
          date: formatDate(item.date),
          dcNumber: item.dcNumber ?? '',
          type: item.transportName ?? item.vehicleType ?? '',
          vehicleprovider: item.vehicleProvider ?? '',
          lorryNumber: '-',
          vechileNo: item.vechileNo ?? item.vehicleNo ?? '-',
          sites: item.sites != null ? String(item.sites) : (item.sites ?? ''),
          startDate: item.startDate != null ? formatDate(item.startDate) : '',
          endDate: item.endDate != null ? formatDate(item.endDate) : '',
          startTime: convertTimeForDisplay(item.timeIn),
          endTime: convertTimeForDisplay(item.timeOut),
          transporter: item.transporterName ?? '',
          startreading: safeStr(item.startingReading),
          endreading: safeStr(item.endingReading),
          totalHours: safeStr(item.totalHours),
          noOfLoad: safeStr(item.numberOfLoads),
          loadWt: '-',
          grossWeight: safeStr(item.grossWeight),
          tareWeight: safeStr(item.tareWeight),
          netWeight: safeStr(item.netWeight),
          totalReading: safeStr(item.totalReading),
          totalAmount: safeNum(item.totalAmount),
          invoiceNumber:
            item.invoiceNumber != null && item.invoiceNumber !== ''
              ? String(item.invoiceNumber)
              : item.invoiceNo != null && item.invoiceNo !== ''
                ? String(item.invoiceNo)
                : '',
          gstNumber:
            item.gstNumber != null && item.gstNumber !== ''
              ? String(item.gstNumber)
              : item.gstNo != null && item.gstNo !== ''
                ? String(item.gstNo)
                : '',
          gstPercentage:
            item.gstPercentage != null && item.gstPercentage !== ''
              ? String(item.gstPercentage)
              : '',
          withGST: computeTransportAmountWithGST(item.totalAmount, item.gstPercentage),
          advance: safeNum(item.advance),
          balance: safeNum(computeTransportBalance(item.totalAmount, item.advance, item.gstPercentage)),
          supervisor: item.supervisorName ?? '',
          noofdays: safeStr(item.numberOfDays),
          numberOfDays: item.numberOfDays != null ? String(item.numberOfDays) : '',
          dayNight: item.shiftType != null ? String(item.shiftType) : ''
        };
        records.push(record);
      });

      records.sort(compareRecordsNewestFirst);
      return records;
    };

    // Helper function to fetch data from API
    const fetchWithCorsFallback = async (url) => {
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
      }
    };

    const fetchTransportData = async () => {
      setIsLoading(true);
      try {
        const apiUrl = `${API_BASE_URL}/api/transports`;
        const apiData = await fetchWithCorsFallback(apiUrl);
        const transformedData = transformApiData(apiData);
        setTransportData(transformedData);
      } catch (error) {
        console.error('Error fetching transport data:', error);
        // Keep existing data if fetch fails
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch data from API
    useEffect(() => {
      fetchTransportData();
    }, []);

    const vehicleProviderOptions = [
    
      { value: 'Rent', label: 'Rent' },
      { value: 'TKD', label: 'TKD' },
    ];

    // Build filterable option lists
    const typeFilterOptions = useMemo(() => {
      return [{ value: 'all', label: 'All' }, ...typeOptions];
    }, []);

    const vehicleProviderFilterOptions = useMemo(() => {
      return [{ value: 'all', label: 'All' }, ...vehicleProviderOptions];
    }, []);

    const supervisorFilterOptions = useMemo(() => {
      if (supervisorsFromApi && supervisorsFromApi.length > 0) {
        return [{ value: 'all', label: 'All' }, ...supervisorsFromApi];
      }
      const supervisors = [...new Set(transportData.map(item => item.supervisor).filter(Boolean))].sort();
      return [{ value: 'all', label: 'All' }, ...supervisors.map(s => ({ value: s, label: s }))];
    }, [transportData, supervisorsFromApi]);

    // Supervisor options for form dropdown
    const supervisorOptions = useMemo(() => {
      if (supervisorsFromApi && supervisorsFromApi.length > 0) {
        return supervisorsFromApi;
      }
      const supervisors = [...new Set(transportData.map(item => item.supervisor).filter(Boolean))].sort();
      return supervisors.map(s => ({ value: s, label: s }));
    }, [transportData, supervisorsFromApi]);

    const dcNumberOptions = useMemo(() => {
      const uniqueDCNumbers = [...new Set(transportData.map(item => item.dcNumber))];
      return [{ value: 'all', label: 'All' }, ...uniqueDCNumbers.map(dc => ({ value: dc, label: dc }))];
    }, [transportData]);

    const siteOptions = maintenanceSiteOptions;

    // Date helpers (same as userDetails.js)
    const isDateInRange = (date, startDate, endDate) => {
      if (!startDate || !endDate || !date) return false;
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      return d >= s && d <= e;
    };

    const parseDateValue = (dateValue) => {
      if (!dateValue) return new Date(0);
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      if (typeof dateValue === 'string') {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const parts = dateValue.split(' ');
        if (parts.length === 3) {
          const month = months.indexOf(parts[0]);
          const day = parseInt(parts[1].replace(',', ''), 10);
          const year = parseInt(parts[2], 10);
          if (month !== -1 && !isNaN(day) && !isNaN(year)) {
            return new Date(year, month, day);
          }
        }
      }
      const parsed = new Date(dateValue);
      return Number.isNaN(parsed.getTime())
        ? new Date(0)
        : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    };

    // Check if any filters are applied
    const hasActiveFilters = useMemo(() => {
      const dcActive = String(dcNumberInputValue ?? '').trim() !== '';
      return datePreset !== '' || dateDisplay !== '' || selectedType !== 'all' || selectedVehicleProvider !== 'all' || selectedSupervisor !== 'all' || dcActive;
    }, [datePreset, dateDisplay, selectedType, selectedVehicleProvider, selectedSupervisor, dcNumberInputValue]);

    // Compute filtered and sorted data
    const filteredData = useMemo(() => {
      let startDate = null;
      let endDate = null;
      const today = new Date();

      if (datePreset && datePreset !== 'custom') {
        switch (datePreset) {
          case 'today': {
            const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            startDate = d;
            endDate = d;
            break;
          }
          case 'yesterday': {
            const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
            startDate = d;
            endDate = d;
            break;
          }
          case 'last7':
            endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
            break;
          case 'last30':
            endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
            break;
          case 'thisMonth':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1);
            endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            break;
          case 'lastMonth':
            startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            endDate = new Date(today.getFullYear(), today.getMonth(), 0);
            break;
          default:
            break;
        }
      } else if (datePreset === 'custom' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      }

      let filtered = transportData.filter(item => {
        const typeOk = selectedType === 'all' || (item.type || '').toString().toLowerCase() === selectedType.toString().toLowerCase();
        const vehicleProviderOk = selectedVehicleProvider === 'all' || (item.vehicleprovider || '').toString().toLowerCase() === selectedVehicleProvider.toString().toLowerCase();
        const supervisorOk = selectedSupervisor === 'all' || (item.supervisor || '').toString().trim().toLowerCase() === selectedSupervisor.toString().trim().toLowerCase();
        const dcNumberSearch = String(dcNumberInputValue ?? '').trim().toLowerCase();
        const dcNumberOk = !dcNumberSearch || (item.dcNumber || '').toString().toLowerCase().includes(dcNumberSearch);

        let dateOk = true;
        if (startDate && endDate) {
          const itemDate = parseDateValue(item.date);
          dateOk = isDateInRange(itemDate, startDate, endDate);
        }

        return typeOk && vehicleProviderOk && supervisorOk && dcNumberOk && dateOk;
      });

      // Apply sorting
      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const sortKey = sortColumn === 'shiftType' ? 'dayNight' : sortColumn;
          let aValue = a[sortKey];
          let bValue = b[sortKey];

          // Handle different data types
          if (sortColumn === 'date') {
            aValue = parseRecordDateTime(a.rawDate || a.date);
            bValue = parseRecordDateTime(b.rawDate || b.date);
          } else if (sortColumn === 'startDate') {
            aValue = parseRecordDateTime(a.rawStartDate || a.startDate);
            bValue = parseRecordDateTime(b.rawStartDate || b.startDate);
          } else if (sortColumn === 'endDate') {
            aValue = parseRecordDateTime(a.rawEndDate || a.endDate);
            bValue = parseRecordDateTime(b.rawEndDate || b.endDate);
          } else if (sortColumn === 'advance' || sortColumn === 'totalAmount' || sortColumn === 'balance' || sortColumn === 'gstPercentage' || sortColumn === 'withGST' || sortColumn === 'noOfLoad' || sortColumn === 'loadWt' || sortColumn === 'numberOfDays' || sortColumn === 'grossWeight' || sortColumn === 'tareWeight' || sortColumn === 'netWeight' || sortColumn === 'totalReading') {
            // Handle numeric values (remove currency symbols)
            aValue = parseFloat((aValue || '').toString().replace(/[₹,%]/g, '')) || 0;
            bValue = parseFloat((bValue || '').toString().replace(/[₹,%]/g, '')) || 0;
          } else {
            // Handle string values
            aValue = (aValue || '').toString().toLowerCase();
            bValue = (bValue || '').toString().toLowerCase();
          }

          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return compareRecordsNewestFirst(a, b);
        });
      }

      return filtered;
    }, [transportData, selectedType, selectedVehicleProvider, selectedSupervisor, dcNumberInputValue, datePreset, customStartDate, customEndDate, sortColumn, sortDirection]);

    // Pagination logic (based on filtered results)
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filteredData.slice(startIndex, endIndex);
    }, [filteredData, currentPage, itemsPerPage]);

    const handlePageChange = (page) => {
      setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); // Reset to first page when changing items per page
    };

    const handleSort = (column) => {
      if (sortColumn === column) {
        // Toggle direction if same column
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        // Set new column; date columns default to newest-first
        setSortColumn(column);
        setSortDirection(defaultSortDirectionForColumn(column));
      }
      setCurrentPage(1); // Reset to first page when sorting
    };

    const getSortIcon = (column) => {
      if (sortColumn !== column) {
        return (
          <svg className="w-4 h-4 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        );
      }
      if (sortDirection === 'asc') {
        return (
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        );
      }
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    };

    const toggleColumn = (columnKey) => {
      const visibleCount = getVisibleColumnCount();
      if (visibleColumns[columnKey] && visibleCount <= 1) {
        // Prevent hiding the last visible column
        return;
      }
      setVisibleColumns(prev => ({
        ...prev,
        [columnKey]: !prev[columnKey]
      }));
    };
    const handleResetColumns = () => {
      setVisibleColumns(defaultVisibleColumns);
    };

    const getVisibleColumnCount = () => {
      return Object.values(visibleColumns).filter(Boolean).length;
    };

    // Close column menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
          setShowColumnMenu(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Date preset display and handling
    useEffect(() => {
      if (!datePreset) {
        setDateDisplay('');
        return;
      }
      
      const now = new Date();
      const toDisplay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
      const clone = (d) => new Date(d.getTime());
      let start = clone(now);
      let end = clone(now);
      
      switch (datePreset) {
        case 'today':
          break;
        case 'yesterday':
          start.setDate(start.getDate() - 1); 
          end.setDate(end.getDate() - 1); 
          break;
        case 'last7':
          start.setDate(start.getDate() - 6); 
          break;
        case 'last30':
          start.setDate(start.getDate() - 29); 
          break;
        case 'thisMonth':
          start = new Date(now.getFullYear(), now.getMonth(), 1); 
          break;
        case 'lastMonth': {
          const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
          start = firstLastMonth; 
          end = lastLastMonth; 
          break;
        }
        case 'custom':
          if (!dateDisplay) {
            setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
          }
          return;
        default:
          break;
      }
      setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
    }, [datePreset]);

    const handleDatePresetChange = (preset) => {
      setDatePreset(preset);
      setShowDateDropdown(false);
      if (preset === 'custom') {
        setShowCustomCalendar(true);
      } else {
        setShowCustomCalendar(false);
      }
    };

    // Calendar helper functions
    const getDaysInMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const formatDate = (date) => {
      if (!date) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };

    const isSameDate = (date1, date2) => {
      if (!date1 || !date2) return false;
      return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    };

    const handleDateClick = (date) => {
      if (!customStartDate || (customStartDate && customEndDate)) {
        setCustomStartDate(date);
        setCustomEndDate(null);
      } else if (customStartDate && !customEndDate) {
        if (date < customStartDate) {
          setCustomEndDate(customStartDate);
          setCustomStartDate(date);
        } else {
          setCustomEndDate(date);
        }
      }
    };

    const handleApplyCustomRange = () => {
      if (customStartDate && customEndDate) {
        setDateDisplay(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
        setShowCustomCalendar(false);
        setShowDateDropdown(false);
      }
    };

    const handleCancelCustomRange = () => {
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
    };

    const handleClearDate = (e) => {
      e.stopPropagation();
      setDatePreset('');
      setDateDisplay('');
      setCustomStartDate(null);
      setCustomEndDate(null);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
    };

    const handleClearType = (e) => {
      e.stopPropagation();
      setSelectedType('all');
      setTypeInputValue('');
      setShowTypeDropdown(false);
      setCurrentPage(1);
    };

    const handleClearVehicleProvider = (e) => {
      e.stopPropagation();
      setSelectedVehicleProvider('all');
      setVehicleProviderInputValue('');
      setShowVehicleProviderDropdown(false);
      setCurrentPage(1);
    };

    const handleClearSupervisor = (e) => {
      e.stopPropagation();
      setSelectedSupervisor('all');
      setSupervisorInputValue('');
      setShowSupervisorDropdown(false);
      setCurrentPage(1);
    };

    const handleClearDCNumber = (e) => {
      e.stopPropagation();
      setSelectedDCNumber('all');
      setDCNumberInputValue('');
      setShowDCNumberDropdown(false);
      setCurrentPage(1);
    };

    const navigateMonth = (direction) => {
      setCurrentMonth(prev => {
        const newMonth = new Date(prev);
        newMonth.setMonth(prev.getMonth() + direction);
        return newMonth;
      });
    };

    // Autocomplete handlers for Type
    const isTypingTypeRef = useRef(false);
    
    useEffect(() => {
      if (!isTypingTypeRef.current) {
        if (selectedType === 'all') {
          setTypeInputValue('');
        } else {
          const selectedOption = typeFilterOptions.find(opt => opt.value === selectedType);
          if (selectedOption) {
            setTypeInputValue(selectedOption.label);
          } else {
            setTypeInputValue('');
          }
        }
      }
    }, [selectedType, typeFilterOptions]);

    const filteredTypeOptions = useMemo(() => {
      if (!typeInputValue.trim()) {
        return typeFilterOptions;
      }
      const searchTerm = typeInputValue.toLowerCase();
      return typeFilterOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm)
      );
    }, [typeFilterOptions, typeInputValue]);

    const handleTypeInputChange = (e) => {
      const value = e.target.value;
      isTypingTypeRef.current = true;
      setTypeInputValue(value);
      setShowTypeDropdown(true);
      
      if (!value.trim()) {
        setSelectedType('all');
      }
      
      setTimeout(() => {
        isTypingTypeRef.current = false;
      }, 100);
    };

    const handleTypeSelect = (option) => {
      isTypingTypeRef.current = false;
      setSelectedType(option.value);
      setTypeInputValue(option.label);
      setShowTypeDropdown(false);
      setCurrentPage(1);
    };

    // Autocomplete handlers for Vehicle Provider
    const isTypingVehicleProviderRef = useRef(false);
    
    useEffect(() => {
      if (!isTypingVehicleProviderRef.current) {
        if (selectedVehicleProvider === 'all') {
          setVehicleProviderInputValue('');
        } else {
          const selectedOption = vehicleProviderFilterOptions.find(opt => opt.value === selectedVehicleProvider);
          if (selectedOption) {
            setVehicleProviderInputValue(selectedOption.label);
          } else {
            setVehicleProviderInputValue('');
          }
        }
      }
    }, [selectedVehicleProvider, vehicleProviderFilterOptions]);

    const filteredVehicleProviderOptions = useMemo(() => {
      if (!vehicleProviderInputValue.trim()) {
        return vehicleProviderFilterOptions;
      }
      const searchTerm = vehicleProviderInputValue.toLowerCase();
      return vehicleProviderFilterOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm)
      );
    }, [vehicleProviderFilterOptions, vehicleProviderInputValue]);

    const handleVehicleProviderInputChange = (e) => {
      const value = e.target.value;
      isTypingVehicleProviderRef.current = true;
      setVehicleProviderInputValue(value);
      setShowVehicleProviderDropdown(true);
      
      if (!value.trim()) {
        setSelectedVehicleProvider('all');
      }
      
      setTimeout(() => {
        isTypingVehicleProviderRef.current = false;
      }, 100);
    };

    const handleVehicleProviderSelect = (option) => {
      isTypingVehicleProviderRef.current = false;
      setSelectedVehicleProvider(option.value);
      setVehicleProviderInputValue(option.label);
      setShowVehicleProviderDropdown(false);
      setCurrentPage(1);
    };

    // Autocomplete handlers for Supervisor
    const isTypingSupervisorRef = useRef(false);
    
    useEffect(() => {
      if (!isTypingSupervisorRef.current) {
        if (selectedSupervisor === 'all') {
          setSupervisorInputValue('');
        } else {
          const selectedOption = supervisorFilterOptions.find(opt => opt.value === selectedSupervisor);
          if (selectedOption) {
            setSupervisorInputValue(selectedOption.label);
          } else {
            setSupervisorInputValue('');
          }
        }
      }
    }, [selectedSupervisor, supervisorFilterOptions]);

    const filteredSupervisorOptions = useMemo(() => {
      if (!supervisorInputValue.trim()) {
        return supervisorFilterOptions;
      }
      const searchTerm = supervisorInputValue.toLowerCase();
      return supervisorFilterOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm)
      );
    }, [supervisorFilterOptions, supervisorInputValue]);

    const handleSupervisorInputChange = (e) => {
      const value = e.target.value;
      isTypingSupervisorRef.current = true;
      setSupervisorInputValue(value);
      setShowSupervisorDropdown(true);
      
      if (!value.trim()) {
        setSelectedSupervisor('all');
      }
      
      setTimeout(() => {
        isTypingSupervisorRef.current = false;
      }, 100);
    };

    const handleSupervisorSelect = (option) => {
      isTypingSupervisorRef.current = false;
      setSelectedSupervisor(option.value);
      setSupervisorInputValue(option.label);
      setShowSupervisorDropdown(false);
      setCurrentPage(1);
    };

    // Autocomplete handlers for DC Number
    const isTypingDCNumberRef = useRef(false);
    
    useEffect(() => {
      if (!isTypingDCNumberRef.current) {
        if (selectedDCNumber === 'all') {
          setDCNumberInputValue('');
        } else {
          const selectedOption = dcNumberOptions.find(opt => opt.value === selectedDCNumber);
          if (selectedOption) {
            setDCNumberInputValue(selectedOption.label);
          } else {
            setDCNumberInputValue('');
          }
        }
      }
    }, [selectedDCNumber, dcNumberOptions]);

    const filteredDCNumberOptions = useMemo(() => {
      if (!dcNumberInputValue.trim()) {
        return dcNumberOptions;
      }
      const searchTerm = dcNumberInputValue.toLowerCase();
      return dcNumberOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm)
      );
    }, [dcNumberOptions, dcNumberInputValue]);

    const handleDCNumberInputChange = (e) => {
      const value = e.target.value;
      isTypingDCNumberRef.current = true;
      setDCNumberInputValue(value);
      setShowDCNumberDropdown(true);
      
      if (!value.trim()) {
        setSelectedDCNumber('all');
      }
      
      setTimeout(() => {
        isTypingDCNumberRef.current = false;
      }, 100);
    };

    const handleDCNumberSelect = (option) => {
      isTypingDCNumberRef.current = false;
      setSelectedDCNumber(option.value);
      setDCNumberInputValue(option.label);
      setShowDCNumberDropdown(false);
      setCurrentPage(1);
    };

    const handleClearFilters = () => {
      setDatePreset('');
      setDateDisplay('');
      setCustomStartDate(null);
      setCustomEndDate(null);
      setSelectedType('all');
      setSelectedVehicleProvider('all');
      setSelectedSupervisor('all');
      setSelectedDCNumber('all');
      setTypeInputValue('');
      setVehicleProviderInputValue('');
      setSupervisorInputValue('');
      setDCNumberInputValue('');
      setCurrentPage(1);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
      setShowTypeDropdown(false);
      setShowVehicleProviderDropdown(false);
      setShowSupervisorDropdown(false);
      setShowDCNumberDropdown(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (showDateDropdown && !event.target.closest('.date-dropdown-container')) {
          setShowDateDropdown(false);
        }
        if (showDCNumberDropdown && !event.target.closest('.dc-number-autocomplete-container')) {
          setShowDCNumberDropdown(false);
        }
        if (showTypeDropdown && !event.target.closest('.type-autocomplete-container')) {
          setShowTypeDropdown(false);
        }
        if (showVehicleProviderDropdown && !event.target.closest('.vehicle-provider-autocomplete-container')) {
          setShowVehicleProviderDropdown(false);
        }
        if (showSupervisorDropdown && !event.target.closest('.supervisor-autocomplete-container')) {
          setShowSupervisorDropdown(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDateDropdown, showTypeDropdown, showVehicleProviderDropdown, showSupervisorDropdown, showDCNumberDropdown]);

    const handleExportPDF = () => {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Transport Report', 14, 15);
      
      // Add date range if available
      if (dateDisplay) {
        doc.setFontSize(10);
        doc.text(`Date Range: ${dateDisplay}`, 14, 22);
      }
      
      // Prepare table data
      const tableData = filteredData.map(item => [
        item.id,
        item.date,
        item.dcNumber,
        item.type,
        item.vehicleprovider,
        item.vechileNo || '-',
        item.transporter,
        item.startTime || '-',
        item.endTime || '-',
        item.startreading || '-',
        item.endreading || '-',
        item.totalHours || '-',
        item.noOfLoad || '-',
        item.totalAmount,
        item.invoiceNumber || '-',
        item.gstNumber || '-',
        item.gstPercentage != null && item.gstPercentage !== '' ? `${String(item.gstPercentage).replace(/%$/, '').trim()}%` : '-',
        item.withGST || '',
        item.advance,
        item.balance,
        item.supervisor,
        item.numberOfDays || '-'
      ]);
      
      // Add table
      autoTable(doc, {
        startY: dateDisplay ? 28 : 22,
        head: [['ID', 'Date', 'DC Number', 'Vehicle Type', 'Vehicle Provider', 'Vehicle #', 'Transporter', 'Start Time', 'End Time', 'Start Reading', 'End Reading', 'Total Hours', 'No of Load(s)', 'Total Amount', 'Invoice Number', 'GST Number', 'GST(%)', 'With GST', 'Advance', 'Balance', 'Supervisor', 'Number of days']],
        body: tableData,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [0, 150, 136], textColor: 255 },
        margin: { top: 20 }
      });
      
      // Save the PDF
      doc.save(`Transport_Report_${new Date().toISOString().slice(0,10)}.pdf`);
    };

    const handleExportExcel = () => {
      const headers = ['ID','Date','DC Number','Vehicle Type','Vehicle Provider','Vehicle #','Transporter Name','Start Time','End Time','Start Reading','End Reading','Total Hours','No of Load(s)','Total Amount','Invoice Number','GST Number','GST(%)','With GST','Advance','Balance','Supervisor','Number of days'];
      const rows = filteredData.map(item => [
        item.id, item.date, item.dcNumber, item.type, item.vehicleprovider, item.vechileNo || '-', item.transporter, item.startTime || '-', item.endTime || '-', item.startreading || '-', item.endreading || '-', item.totalHours || '-', item.noOfLoad || '-', item.totalAmount,
        item.invoiceNumber || '', item.gstNumber || '', item.gstPercentage != null && item.gstPercentage !== '' ? item.gstPercentage : '',
        item.withGST || '',
        item.advance, item.balance, item.supervisor, item.numberOfDays || '-'
      ]);
      
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transport Report');
      
      // Set column widths
      const columnWidths = [
        { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 18 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 10 }, { wch: 14 }, { wch: 16 }, { wch: 8 }, { wch: 11 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }
      ];
      worksheet['!cols'] = columnWidths;
      
      XLSX.writeFile(workbook, `Transport_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const handleGenerateReport = () => {
      // Export filtered rows to CSV
      const headers = ['ID','Date','DC Number','Vehicle Type','Vehicle Provider','Vehicle #','Transporter Name','Start Time','End Time','Start Reading','End Reading','Total Hours','Total Amount','Invoice Number','GST Number','GST(%)','With GST','Advance','Balance','Supervisor','Number of days'];
      const rows = filteredData.map(item => [
        item.id, item.date, item.dcNumber, item.type, item.vehicleprovider, item.vechileNo || '', item.transporter, item.startTime || '', item.endTime || '', item.startreading, item.endreading, item.totalHours, item.totalAmount,
        item.invoiceNumber || '', item.gstNumber || '', item.gstPercentage != null && item.gstPercentage !== '' ? item.gstPercentage : '',
        item.withGST || '',
        item.advance, item.balance, item.supervisor, item.numberOfDays || ''
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => (v == null ? '' : String(v).replace(/"/g,'""'))).map(v => `"${v}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Transport_Report_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        // Auto-calculate Balance when Total Amount / Advance / GST(%) changes
        if (name === 'totalAmount' || name === 'advance' || name === 'gstPercentage') {
          const totalStr = name === 'totalAmount' ? value : newData.totalAmount;
          const advanceStr = name === 'advance' ? value : newData.advance;
          const gstPctStr = name === 'gstPercentage' ? value : newData.gstPercentage;
          newData.balance = resolveTransportBalance(totalStr, advanceStr, gstPctStr);
          newData.withGST = computeTransportAmountWithGST(totalStr, gstPctStr);
        }
        // Handle vehicleprovider changes
        if (name === 'vehicleprovider') {
          if (value === 'Own') {
            // If TKD is selected, set transporter to "TKD" by default
            newData.transporter = 'TKD';
          } else if (value === 'Rent') {
            // If Rent is selected, keep transporter as is (user can enter)
            // Don't clear it
            newData.transporter = 'Rent';  
          } else {
            // If cleared or other value, clear transporter
            newData.transporter = '';
          }
        }
        // Auto-calculate Net Weight for Tipper
        if (name === 'grossWeight' || name === 'tareWeight') {
          const gross = name === 'grossWeight' ? parseFloat(value) : parseFloat(newData.grossWeight);
          const tare = name === 'tareWeight' ? parseFloat(value) : parseFloat(newData.tareWeight);
          if (!isNaN(gross) && !isNaN(tare) && gross >= tare) {
            newData.netWeight = gross - tare;
          } else {
            newData.netWeight = '';
          }
        }
        if (name === 'gstNumber') {
          newData.gstNumber = formatIndianGstNumber(value);
        }
        return newData;
      });
    };

    const handleNumberInput = (e) => {
      // Allow: backspace, delete, tab, escape, enter, decimal point
      if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
        return;
      }
      // Ensure that it is a number and stop the keypress
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    };

    const calculateTotalHours = (startHour, endHour) => {
      if (!startHour || !endHour) return '';
      
      // Parse numeric hour values (e.g., 2135.4, 2142.0)
      const start = parseFloat(startHour);
      const end = parseFloat(endHour);
      
      if (isNaN(start) || isNaN(end)) return 'Invalid';
      if (start >= end) return 'Invalid';
      
      // Calculate difference in hours
      const totalHours = end - start;
      
      // Format as hours with 1 decimal place
      return totalHours.toFixed(1);
    };

    // Convert 12-hour format to 24-hour format for display in time inputs
    const convertTo24Hour = (time12h) => {
      if (!time12h) return '';
      const [time, period] = time12h.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      
      if (period === 'AM' && hours === 12) hours = 0;
      if (period === 'PM' && hours !== 12) hours += 12;
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    // Convert 24-hour format to 12-hour format for display in table
    const convertTo12Hour = (time24h) => {
      if (!time24h) return '';
      const [hours, minutes] = time24h.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      let displayHours = hours % 12;
      if (displayHours === 0) displayHours = 12;
      
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Validate required fields
      if (!formData.type) {
        alert('Please select a Vehicle Type');
        return;
      }
      
      if (!formData.vehicleprovider) {
        alert('Please select a Vehicle Provider');
        return;
      }
      
      // Validate Transporter Name when Rent is selected
      if (formData.vehicleprovider === 'Rent' && !formData.transporter) {
        alert('Please enter Transporter Name');
        return;
      }

      if (!formData.supervisor) {
        alert('Please select Supervisor');
        return;
      }

      const gstNoRaw = String(formData.gstNumber ?? '').trim();
      if (gstNoRaw && !isValidIndianGstNumber(gstNoRaw)) {
        alert('Please enter a valid GST Number (15 characters, e.g. 33AAACH7409R1Z8)');
        return;
      }
      
      // Calculate total hours for non-Tipper types
      let calculatedHours = '';
      if (formData.type === 'JCB') {
        // Validate Hours and Day/Night for JCB
        if (!formData.totalHours) {
          alert('Please enter Hours');
          return;
        }
        
        if (!formData.dayNight) {
          alert('Please select Day/Night');
          return;
        }
        
        // Validate Start Reading and End Reading for JCB
        // if (!formData.startreading) {
        //   alert('Please enter Start Reading');
        //   return;
        // }
        
        // if (!formData.endreading) {
        //   alert('Please enter End Reading');
        //   return;
        // }

        // Calculate total hours from readings (for display/validation)
        calculatedHours = calculateTotalHours(formData.startreading, formData.endreading);
        if (calculatedHours === 'Invalid') {
          alert('End reading must be greater than start reading');
          return;
        }
      } else  if (formData.type === 'Poklaine' || formData.type === 'Bracker' || formData.type === 'Bobcat Bucket' || formData.type === 'Bobcat Breaker') {
        // Validate Start Reading and End Reading for Poklaine/Bracker
        // if (!formData.startreading) {
        //   alert('Please enter Start Reading');
        //   return;
        // }
        
        // if (!formData.endreading) {
        //   alert('Please enter End Reading');
        //   return;
        // }

        // Calculate total hours
        calculatedHours = calculateTotalHours(formData.startreading, formData.endreading);
        if (calculatedHours === 'Invalid') {
          alert('End reading must be greater than start reading');
          return;
        }
      } else if (formData.type === 'Tipper') {
        // Validate weight fields for Tipper (only if provided)
        if (formData.grossWeight && formData.tareWeight) {
          const gross = parseFloat(formData.grossWeight);
          const tare = parseFloat(formData.tareWeight);
          
          if (isNaN(gross) || isNaN(tare)) {
            alert('Please enter valid weight values');
            return;
          }
          
          if (gross < tare) {
            alert('Gross Weight must be greater than or equal to Tare Weight');
            return;
          }
        }
      }

      // For new records, call API to create transport
      if (!isEditMode) {
        try {
          // Format date to YYYY-MM-DD for API
          const apiDate = formData.date || new Date().toISOString().split('T')[0];
          
          // Helper function to convert time string (HH:MM) to HH:MM:SS format
          const convertTimeToString = (timeStr) => {
            if (!timeStr) {
              return '00:00:00';
            }
            const parts = timeStr.split(':');
            const hour = String(parseInt(parts[0] || 0, 10)).padStart(2, '0');
            const minute = String(parseInt(parts[1] || 0, 10)).padStart(2, '0');
            return `${hour}:${minute}:00`;
          };

          // Map vehicleprovider: "Own" -> "TKD" for API
          const vehicleProviderForAPI = formData.vehicleprovider === 'Own' ? 'TKD' : formData.vehicleprovider || '';

          const startRead = parseFloat(formData.startreading) || 0;
          const endRead = parseFloat(formData.endreading) || 0;
          const totalReading = endRead - startRead;

          const gstPctRaw = String(formData.gstPercentage ?? '').trim();
          let gstPercentagePayload = null;
          if (gstPctRaw !== '') {
            const n = parseFloat(gstPctRaw);
            gstPercentagePayload = Number.isFinite(n) ? n : null;
          }

          // Prepare request body matching API format
          const isJcbOrTipper = formData.type === 'JCB' || formData.type === 'Tipper';
          const requestBody = {
            id: 0,
            advance: parseFloat(formData.advance) || 0,
            balance: parseFloat(formData.balance) || 0,
            date: apiDate,
            dcNumber: formData.dcNumber || '',
            invoiceNumber: formData.invoiceNumber || '',
            gstNumber: formData.gstNumber || '',
            gstPercentage: gstPercentagePayload,
            endDate: isJcbOrTipper ? '' : (formData.endDate || apiDate),
            endingReading: String(formData.endreading || ''),
            grossWeight: parseFloat(formData.grossWeight) || 0,
            netWeight: parseFloat(formData.netWeight) || 0,
            numberOfDays: parseInt(formData.numberOfDays, 10) || 0,
            numberOfLoads: parseInt(formData.noOfLoad, 10) || 0,
            shiftType: formData.dayNight || '',
            sites: formData.site || '',
            startDate: isJcbOrTipper ? '' : (formData.startDate || apiDate),
            startingReading: String(formData.startreading || ''),
            supervisorName: formData.supervisor || '',
            tareWeight: parseFloat(formData.tareWeight) || 0,
            timeIn: formData.type === 'JCB' ? '' : convertTimeToString(formData.startTime),
            timeOut: formData.type === 'JCB' ? '' : convertTimeToString(formData.endTime),
            totalAmount: parseFloat(formData.totalAmount) || 0,
            totalHours: String(formData.totalHours || ''),
            totalReading: totalReading,
            transporterName: formData.transporter || 'TKD',
            transportName: formData.type || '',
            vechileNo: formData.vechileNo || '',
            vehicleProvider: vehicleProviderForAPI,
            vehicleType: formData.type || ''
          };

          // Use CORS proxy for the API call
          const apiUrl = `${API_BASE_URL}/api/transports`;
          
          // For POST requests, try direct fetch first
          let response;
          try {
            response = await fetch(apiUrl, {
              method: 'POST',
              mode: 'cors',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            });
          } catch (corsError) {
            // If CORS fails, we'll need to use a backend proxy or handle differently
            console.error('CORS Error:', corsError);
            alert('CORS Error: Unable to connect to the API server. Please configure CORS on the backend or use a backend proxy.');
            return;
          }
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
          }
          
          const responseData = await response.json();
          console.log('Transport created successfully:', responseData);
          
          // Refresh transport data from API to update the grid table
          try {
            const transportApiUrl = `${API_BASE_URL}/api/transports`;
            const apiData = await fetchWithCorsFallback(transportApiUrl);
            const transformedData = transformApiData(apiData);
            setTransportData(transformedData);
            // Reset to first page to show the new record
            setCurrentPage(1);
          } catch (refreshError) {
            console.error('Error refreshing data:', refreshError);
            // Still show success even if refresh fails
          }
          
          setSuccessMessage('Transport added successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          handleClose();
          return;
        } catch (error) {
          console.error('Error creating transport:', error);
          setError(error.message || 'Failed to create transport');
          setSuccessMessage(`Failed to create transport: ${error.message}`);
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          return;
        }
      }

      // Helper function to convert time string (HH:MM) to HH:MM:SS format
      const convertTimeToString = (timeStr) => {
        if (!timeStr) {
          return '00:00:00';
        }
        const parts = timeStr.split(':');
        const hour = String(parseInt(parts[0] || 0, 10)).padStart(2, '0');
        const minute = String(parseInt(parts[1] || 0, 10)).padStart(2, '0');
        return `${hour}:${minute}:00`;
      };

      // For Edit mode, call API to update transport
      if (isEditMode) {
        try {
          if (!editingTransportId) {
            alert('Error: Transport ID not found');
            return;
          }

          // Format date to YYYY-MM-DD for API
          const apiDate = formData.date || new Date().toISOString().split('T')[0];
          
          // Map vehicleprovider: "Own" -> "TKD" for API
          const vehicleProviderForAPI = formData.vehicleprovider === 'Own' ? 'TKD' : formData.vehicleprovider || '';

          const startRead = parseFloat(formData.startreading) || 0;
          const endRead = parseFloat(formData.endreading) || 0;
          const totalReading = endRead - startRead;

          const gstPctRawPut = String(formData.gstPercentage ?? '').trim();
          let gstPercentagePayloadPut = null;
          if (gstPctRawPut !== '') {
            const n = parseFloat(gstPctRawPut);
            gstPercentagePayloadPut = Number.isFinite(n) ? n : null;
          }

          // Prepare request body matching API format
          const isJcbOrTipper = formData.type === 'JCB' || formData.type === 'Tipper';
          const requestBody = {
            advance: parseFloat(formData.advance) || 0,
            balance: parseFloat(formData.balance) || 0,
            date: apiDate,
            dcNumber: formData.dcNumber || '',
            invoiceNumber: formData.invoiceNumber || '',
            gstNumber: formData.gstNumber || '',
            gstPercentage: gstPercentagePayloadPut,
            endDate: isJcbOrTipper ? '' : (formData.endDate || apiDate),
            endingReading: String(formData.endreading || ''),
            grossWeight: parseFloat(formData.grossWeight) || 0,
            id: editingTransportId,
            netWeight: parseFloat(formData.netWeight) || 0,
            numberOfDays: parseInt(formData.numberOfDays, 10) || 0,
            numberOfLoads: parseInt(formData.noOfLoad, 10) || 0,
            shiftType: formData.dayNight || '',
            sites: formData.site || '',
            startDate: isJcbOrTipper ? '' : (formData.startDate || apiDate),
            startingReading: String(formData.startreading || ''),
            supervisorName: formData.supervisor || '',
            tareWeight: parseFloat(formData.tareWeight) || 0,
            timeIn: formData.type === 'JCB' ? '' : convertTimeToString(formData.startTime),
            timeOut: formData.type === 'JCB' ? '' : convertTimeToString(formData.endTime),
            totalAmount: parseFloat(formData.totalAmount) || 0,
            totalHours: formData.type === 'JCB' ? String(formData.totalHours || '') : String(calculatedHours || ''),
            totalReading: totalReading,
            transporterName: formData.transporter || '',
            transportName: formData.type || '',
            vechileNo: formData.vechileNo || '',
            vehicleProvider: vehicleProviderForAPI,
            vehicleType: formData.type || ''
          };

          const apiUrl = `${API_BASE_URL}/api/transports`;
          
          const response = await fetch(apiUrl, {
            method: 'PUT',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
          }

          const responseData = await response.json();
          console.log('Transport updated successfully:', responseData);
          
          // Refresh transport data from API to update the grid table
          try {
            const transportApiUrl = `${API_BASE_URL}/api/transports`;
            const apiData = await fetchWithCorsFallback(transportApiUrl);
            const transformedData = transformApiData(apiData);
            setTransportData(transformedData);
            // Reset to first page to show the updated record
            setCurrentPage(1);
          } catch (refreshError) {
            console.error('Error refreshing data:', refreshError);
            // Still show success even if refresh fails
          }
          
          setSuccessMessage('Transport updated successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          handleClose();
          return;
        } catch (error) {
          console.error('Error updating transport:', error);
          setError(error.message || 'Failed to update transport');
          setSuccessMessage(`Failed to update transport: ${error.message}`);
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          return;
        }
      }
      
      handleClose();
    };

    const handleClose = () => {
      setIsModalOpen(false);
      setIsEditMode(false);
      setEditingId(null);
      setEditingTransportId(null);
      setFormData({
        date: getTodayDate(),
        dcNumber: '',
        site: '',
        transporter: '',
        type: '',
        vehicleprovider: '',
        vechileNo: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        startreading: '',
        endreading: '',
        grossWeight: '',
        tareWeight: '',
        netWeight: '',
        totalHours: '',
        hours: '',
        dayNight: '',
        totalAmount: '',
        withGST: '',
        invoiceNumber: '',
        gstNumber: '',
        gstPercentage: '',
        advance: '',
        balance: '',
        supervisor: '',
        numberOfDays: ''
      });
    };

    const handleEdit = (id) => {
      const record = transportData.find(item => item.id === id);
      if (record) {
        // Convert date from "Oct 01, 2025" format to YYYY-MM-DD format
        const convertDateToInputFormat = (dateStr) => {
          if (!dateStr) return getTodayDate();
          // Check if already in YYYY-MM-DD format
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateStr;
          }
          // Parse "Oct 01, 2025" format
          const months = {
            'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
            'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
            'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
          };
          const match = dateStr.match(/(\w+)\s+(\d+),\s+(\d+)/);
          if (match) {
            const [, month, day, year] = match;
            const monthNum = months[month] || '01';
            return `${year}-${monthNum}-${day.padStart(2, '0')}`;
          }
          return getTodayDate();
        };

        // Map vehicleprovider: "TKD" -> "Own" for dropdown
        const vehicleProviderValue = record.vehicleprovider === 'TKD' ? 'Own' : record.vehicleprovider || '';
        
        // If vehicleprovider is TKD/Own, set transporter to "TKD" by default, otherwise use the record value
        const transporterValue = vehicleProviderValue === 'Own' ? 'TKD' : (record.transporter || '');

        // Convert time from display format to input format (HH:MM or '-' to HH:MM or '')
        const convertTimeForInput = (timeStr) => {
          if (!timeStr || timeStr === '-' || timeStr === 'null') return '';
          // If already in HH:MM format, return as is
          if (timeStr.match(/^\d{2}:\d{2}$/)) return timeStr;
          // If in HH:MM:SS format, extract HH:MM
          if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
            return timeStr.substring(0, 5);
          }
          return timeStr;
        };

        setFormData({
          date: convertDateToInputFormat(record.date),
          dcNumber: record.dcNumber || '',
          site: record.sites ?? record.site ?? '',
          transporter: transporterValue,
          type: record.type || '',
          vehicleprovider: vehicleProviderValue,
          vechileNo: record.vechileNo || '',
          startDate: record.startDate ? convertDateToInputFormat(record.startDate) : '',
          endDate: record.endDate ? convertDateToInputFormat(record.endDate) : '',
          startTime: convertTimeForInput(record.startTime),
          endTime: convertTimeForInput(record.endTime),
          startreading: record.startreading === '-' ? '' : (record.startreading || ''), // Convert '-' to empty string
          endreading: record.endreading === '-' ? '' : (record.endreading || ''), // Convert '-' to empty string
          grossWeight: record.grossWeight === '-' ? '' : (record.grossWeight || ''),
          tareWeight: record.tareWeight === '-' ? '' : (record.tareWeight || ''),
          netWeight: record.netWeight === '-' ? '' : (record.netWeight || ''),
          totalHours: record.totalHours === '-' ? '' : (record.totalHours || ''), // Convert '-' to empty string
          dayNight: record.dayNight || record.daynight || '',
          totalAmount: record.totalAmount || '',
          withGST: record.withGST || computeTransportAmountWithGST(record.totalAmount, record.gstPercentage),
          invoiceNumber: record.invoiceNumber || '',
          gstNumber: formatIndianGstNumber(record.gstNumber || ''),
          gstPercentage: record.gstPercentage != null && record.gstPercentage !== '' ? String(record.gstPercentage) : '',
          advance: record.advance || '',
          balance: resolveTransportBalance(record.totalAmount, record.advance, record.gstPercentage),
          supervisor: record.supervisor || '',
          numberOfDays: record.numberOfDays || record.noofdays || '',
          noOfLoad: record.noOfLoad || ''
        });
        setEditingId(id);
        setEditingTransportId(record.transportId); // Store transportId for API update
        setIsEditMode(true);
        setIsModalOpen(true);
      }
    };

    const handleDelete = (id) => {
      // Find the record to get the transportId
      const record = transportData.find(item => item.id === id);
      if (record) {
        setRecordToDelete(record.transportId);
        setShowDeleteConfirm(true);
      }
    };

    const handleConfirmDelete = async () => {
      if (!recordToDelete) return;
      
      try {
        const apiUrl = `${API_BASE_URL}/api/transports/${recordToDelete}`;
        
        const response = await fetch(apiUrl, {
          method: 'DELETE',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        console.log('Transport deleted successfully');
        
        // Refresh transport data from API to update the grid table
        try {
          const transportApiUrl = `${API_BASE_URL}/api/transports`;
          const apiData = await fetchWithCorsFallback(transportApiUrl);
          const transformedData = transformApiData(apiData);
          setTransportData(transformedData);
          // Reset to first page
          setCurrentPage(1);
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
          // Still show success even if refresh fails
        }
        
        setSuccessMessage('Transport record deleted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setShowDeleteConfirm(false);
        setRecordToDelete(null);
      } catch (error) {
        console.error('Error deleting transport record:', error);
        setError(error.message || 'Failed to delete transport record');
        setSuccessMessage(`Failed to delete transport record: ${error.message}`);
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    };

    const handleCancelDelete = () => {
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    };

    return (
      <>
        {/* Success Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          style={{position: 'absolute', bottom: '10px', left: '10px'}}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity}
            sx={{ 
              width: '100%', 
              backgroundColor: snackbarSeverity === 'success' ? '#43a047' : '#dc2626', 
              color: '#fff', 
              fontWeight: '400',
              fontSize: '18px' 
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Error Message */}
        {error && (
          <div className="bg-red-200 border border-red-400 text-red-700 px-4 py-3 rounded" style={{position: 'absolute', bottom: '10px', left: '10px', zIndex: '1000', minWidth: '200px'}}>
            {error}
          </div>
        )}
        
        <div className="space-y-6">
        
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 font-poppins">Transport</h1>
          
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={fetchTransportData}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Refresh"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 00-14.828-3M4 16a8 8 0 0014.828 3" />
              </svg>
            
            </button>
            <div className="relative" ref={columnMenuRef}>
              <button
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Show/Hide Columns"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
      
              </button>
              {showColumnMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                      Toggle Columns
                    </div>
                    <div className="px-3 py-2 border-b border-gray-200">
                      <button
                        type="button"
                        onClick={handleResetColumns}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Reset
                      </button>
                    </div>
                    {columnDefinitions.map((col) => {
                      const isLastVisible = visibleColumns[col.key] && getVisibleColumnCount() === 1;
                      return (
                        <label
                          key={col.key}
                          className={`flex items-center px-3 py-2 hover:bg-gray-100 ${
                            isLastVisible ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                          }`}
                          title={isLastVisible ? 'At least one column must remain visible' : ''}
                        >
                          <input
                            type="checkbox"
                            checked={visibleColumns[col.key]}
                            onChange={() => toggleColumn(col.key)}
                            disabled={isLastVisible}
                            className="mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:cursor-not-allowed"
                          />
                          <span className={`text-sm ${isLastVisible ? 'text-gray-500' : 'text-gray-700'}`}>{col.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {isAdministrator && (
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title={showFilters ? "Hide Filters" : "Show Filters"}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                </svg>
                <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
              </button>
            )}
            <button 
              onClick={() => {
                setIsEditMode(false);
                // Set current date when opening add modal
                setFormData(prev => ({
                  ...prev,
                  date: getTodayDate()
                }));
                setIsModalOpen(true);
              }}
              className="btn-primary"
            >
              Add Transport
            </button>
          </div>
        </div>

        {isAdministrator && showFilters && (
          <div className="p-4 flex flex-wrap items-end gap-4 bg-white shadow rounded-lg" style={{marginTop: '10px'}}>
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">
              {datePreset == '' ? 'Choose Date' : 'Selected Date Range: '} 
              {datePreset && <span className="font-bold text-black text-xs uppercase">({datePreset == "today" ? "Today" : datePreset == "yesterday" ? "Yesterday" : datePreset == "last7" ? "Last 7 Days" : datePreset == "last30" ? "Last 30 Days" : datePreset == "thisMonth" ? "This Month" : datePreset == "lastMonth" ? "Last Month" : datePreset == "custom" ? "Custom Range" : datePreset})</span>}
              </label>
              <div className="relative date-dropdown-container">
                <div className="relative">
                  <input
                    type="text"
                    value={dateDisplay || 'Select Range'}
                    readOnly
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                    className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Select Date Range"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  {dateDisplay && (
                    <button
                      type="button"
                      onClick={handleClearDate}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                      title="Clear date"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                  {showDateDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-20">
                      <div className="py-1">
                        <button
                          onClick={() => handleDatePresetChange('today')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Today
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('yesterday')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Yesterday
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('last7')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Last 7 Days
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('last30')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Last 30 Days
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('thisMonth')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          This Month
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('lastMonth')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Last Month
                        </button>
                        <button
                          onClick={() => handleDatePresetChange('custom')}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                        >
                          Custom Range
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Custom Calendar Modal */}
                  {showCustomCalendar && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                      <div className="flex">
                        {/* Left side - Preset options */}
                        <div className="w-48 pr-4 border-r border-gray-200">
                          <div className="space-y-1">
                            <button
                              onClick={() => handleDatePresetChange('today')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Today
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('yesterday')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Yesterday
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('last7')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Last 7 Days
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('last30')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Last 30 Days
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('thisMonth')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              This Month
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('lastMonth')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Last Month
                            </button>
                            <button
                              onClick={() => handleDatePresetChange('custom')}
                              className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                              Custom Range
                            </button>
                          </div>
                        </div>
                        
                        {/* Right side - Calendar */}
                        <div className="ml-4">
                          {/* Calendar Navigation */}
                          <div className="flex items-center justify-between ">
                            <button
                              onClick={() => navigateMonth(-1)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                            <h3 className="text-sm font-medium text-gray-900">
                              {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </h3>
                            <button
                              onClick={() => navigateMonth(1)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Calendar Grid */}
                          <div className="w-64">
                            {/* Days of week header */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                                <div key={day} className="text-xs text-gray-500 text-center py-1">
                                  {day}
                                </div>
                              ))}
                            </div>
                            
                            {/* Calendar days */}
                            <div className="grid grid-cols-7 gap-1">
                              {(() => {
                                const daysInMonth = getDaysInMonth(currentMonth);
                                const firstDay = getFirstDayOfMonth(currentMonth);
                                const days = [];
                                
                                // Previous month days
                                const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                                for (let i = firstDay - 1; i >= 0; i--) {
                                  const day = prevMonth.getDate() - i;
                                  days.push(
                                    <button
                                      key={`prev-${day}`}
                                      className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                      disabled
                                    >
                                      {day}
                                    </button>
                                  );
                                }
                                
                                // Current month days
                                for (let day = 1; day <= daysInMonth; day++) {
                                  const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                                  const isSelected = isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                                  const isInRange = customStartDate && customEndDate && isDateInRange(date, customStartDate, customEndDate);
                                  
                                  days.push(
                                    <button
                                      key={day}
                                      onClick={() => handleDateClick(date)}
                                      className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                        isSelected 
                                          ? 'bg-orange-500 text-white' 
                                          : isInRange 
                                          ? 'bg-orange-100 text-orange-600' 
                                          : 'text-gray-700'
                                      }`}
                                    >
                                      {day}
                                    </button>
                                  );
                                }
                                
                                // Next month days
                                const remainingCells = 42 - days.length; // 6 rows * 7 days
                                for (let day = 1; day <= remainingCells; day++) {
                                  days.push(
                                    <button
                                      key={`next-${day}`}
                                      className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                      disabled
                                    >
                                      {day}
                                    </button>
                                  );
                                }
                                
                                return days;
                              })()}
                            </div>
                          </div>
                          
                          {/* Selected date range display */}
                          <div className="mt-4 text-center">
                            <div className="text-sm text-gray-600 mb-3">
                              {customStartDate && customEndDate 
                                ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                                : customStartDate 
                                ? `${formatDate(customStartDate)} - Select end date`
                                : 'Select start date'
                              }
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={handleCancelCustomRange}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleApplyCustomRange}
                                disabled={!customStartDate || !customEndDate}
                                className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col max-w-[180px]">
                <label className="text-xs text-gray-600 mb-1">Type</label>
                <div className="relative type-autocomplete-container">
                  <input
                    type="text"
                    value={typeInputValue}
                    onChange={handleTypeInputChange}
                    onFocus={() => {
                      setShowTypeDropdown(true);
                    }}
                    placeholder="Search "
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {typeInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearType}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear type"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {showTypeDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      <div className="py-1">
                        {filteredTypeOptions.length > 0 ? (
                          filteredTypeOptions.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleTypeSelect(opt)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                                selectedType === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col max-w-[180px]">
                <label className="text-xs text-gray-600 mb-1">Vehicle Provider</label>
                <div className="relative vehicle-provider-autocomplete-container">
                  <input
                    type="text"
                    value={vehicleProviderInputValue}
                    onChange={handleVehicleProviderInputChange}
                    onFocus={() => {
                      setShowVehicleProviderDropdown(true);
                    }}
                    placeholder="Search "
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {vehicleProviderInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearVehicleProvider}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear vehicle provider"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {showVehicleProviderDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      <div className="py-1">
                        {filteredVehicleProviderOptions.length > 0 ? (
                          filteredVehicleProviderOptions.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleVehicleProviderSelect(opt)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                                selectedVehicleProvider === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col max-w-[180px]">
                <label className="text-xs text-gray-600 mb-1">Supervisor</label>
                <div className="relative supervisor-autocomplete-container">
                  <input
                    type="text"
                    value={supervisorInputValue}
                    onChange={handleSupervisorInputChange}
                    onFocus={() => {
                      setShowSupervisorDropdown(true);
                    }}
                    placeholder="Search "
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {supervisorInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearSupervisor}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear supervisor"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {showSupervisorDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      <div className="py-1">
                        {filteredSupervisorOptions.length > 0 ? (
                          filteredSupervisorOptions.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleSupervisorSelect(opt)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                                selectedSupervisor === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
                  <div className="flex flex-col max-w-[180px]">
                <label className="text-xs text-gray-600 mb-1">DC Number</label>
                <div className="relative dc-number-autocomplete-container">
                  <input
                    type="text"
                    value={dcNumberInputValue}
                    onChange={handleDCNumberInputChange}
                    placeholder="Enter DC Number"
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    {dcNumberInputValue ? (
                      <button
                        type="button"
                        onClick={handleClearDCNumber}
                        className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                        title="Clear DC number"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : (
                      <div className="pr-3 pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button 
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                  title="Clear Filters"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Clear All</span>
                </button>
                <button 
                  onClick={handleExportPDF}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  title="Export as PDF"
                >
                  <img src="/assets/img/pdf.png" alt="PDF" className="h-4 w-4" />
                </button>
                <button 
                  onClick={handleExportExcel}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  title="Export as Excel"
                >
                  <img src="/assets/img/sheet.png" alt="Excel" className="h-4 w-4" />
                </button>
              </div>
            </div>
        )}

        {isLoading ? (
          <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary "></div>
              <p className="text-gray-600 text-sm">Loading data...</p>
            </div>
          </div>
        ) : (
          <Grid cols={1} className="bg-white shadow rounded-lg">
            <div className="">
              <div className="overflow-x-auto">
                <div 
                  className={`relative overflow-y-auto ${!showFilters ? 'bg-white' : ''}`}
                  style={isAdministrator ? { height: 'calc(100vh - 370px)', backgroundColor: '#fff' } :  { height: 'calc(100vh - 270px)', backgroundColor: '#fff' }
                  }
                >
                  <table className="min-w-full divide-y divide-gray-200 shadow rounded-lg border border-gray-200">
                    <thead className="sticky top-0 z-10" style={{backgroundColor: "rgb(0, 150, 136)"}}>
                  <tr>
                    {visibleColumns.date && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('date')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Date</span>
                          {getSortIcon('date')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.dcNumber && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('dcNumber')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>DC Number</span>
                          {getSortIcon('dcNumber')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.sites && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('sites')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Site</span>
                          {getSortIcon('sites')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.type && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('type')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Vehicle Type</span>
                          {getSortIcon('type')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.vehicleprovider && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('vehicleprovider')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Vehicle Provider</span>
                          {getSortIcon('vehicleprovider')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.vechileNo && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Vehicle #</th>
                    )}
                    {visibleColumns.transporter && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('transporter')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Transporter Name</span>
                          {getSortIcon('transporter')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.startTime && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Start Time</th>
                    )}
                    {visibleColumns.endTime && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>End Time</th>
                    )}
                    {visibleColumns.startDate && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('startDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Start Date</span>
                          {getSortIcon('startDate')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.endDate && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('endDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>End Date</span>
                          {getSortIcon('endDate')}
                        </div>
                      </th>
                    )}
                    {/* Conditional headers based on vehicle type */}
                    {transportData.some(record => record.type !== 'Tipper') && visibleColumns.startreading && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Start Reading</th>
                    )}
                    {transportData.some(record => record.type !== 'Tipper') && visibleColumns.endreading && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>End Reading</th>
                    )}
                    {transportData.some(record => record.type !== 'Tipper') && visibleColumns.totalHours && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Total Hours</th>
                    )}
                    {visibleColumns.totalReading && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Total Reading</th>
                    )}
                    {visibleColumns.shiftType && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('shiftType')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Shift Type</span>
                          {getSortIcon('shiftType')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.noOfLoad && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('noOfLoad')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Number of Loads</span>
                          {getSortIcon('noOfLoad')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.numberOfDays && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('numberOfDays')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Number of days</span>
                          {getSortIcon('numberOfDays')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.grossWeight && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Gross Weight</th>
                    )}
                    {visibleColumns.tareWeight && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Tare Weight</th>
                    )}
                    {visibleColumns.netWeight && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Net Weight</th>
                    )}
                    {transportData.some(record => record.type === 'Tipper') && visibleColumns.loadWt && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Load Wt(Tonnage)</th>
                    )}
                    {visibleColumns.invoiceNumber && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                        onClick={() => handleSort('invoiceNumber')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Invoice Number</span>
                          {getSortIcon('invoiceNumber')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.gstNumber && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                        onClick={() => handleSort('gstNumber')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>GST Number</span>
                          {getSortIcon('gstNumber')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.totalAmount && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('totalAmount')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Total Amount</span>
                          {getSortIcon('totalAmount')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.gstPercentage && (
                      <th
                        className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                        onClick={() => handleSort('gstPercentage')}
                      >
                        <div className="flex items-center justify-end space-x-1">
                          <span>GST(%)</span>
                          {getSortIcon('gstPercentage')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.withGST && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                        onClick={() => handleSort('withGST')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>With GST</span>
                          {getSortIcon('withGST')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.advance && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('advance')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Advance</span>
                          {getSortIcon('advance')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.balance && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('balance')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Balance</span>
                          {getSortIcon('balance')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.supervisor && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('supervisor')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Supervisor</span>
                          {getSortIcon('supervisor')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.actions && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" style={{maxHeight: '600px', overflowY: 'auto'}}>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="px-6 py-4 text-center text-sm text-gray-500">
                        No transport records found
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        {visibleColumns.date && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.date}</td>}
                        {visibleColumns.dcNumber && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.dcNumber}</td>}
                        {visibleColumns.sites && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.sites || '-'}</td>}
                        {visibleColumns.type && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.type}</td>}
                        {visibleColumns.vehicleprovider && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full `}>
                              {record.vehicleprovider}
                            </span>
                          </td>
                        )}
                        {visibleColumns.vechileNo && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.vechileNo || '-'}</td>}
                        {visibleColumns.transporter && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.transporter}</td>}
                        {visibleColumns.startTime && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.startTime || '-'}</td>}
                        {visibleColumns.endTime && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.endTime || '-'}</td>}
                        {visibleColumns.startDate && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.startDate || '-'}</td>}
                        {visibleColumns.endDate && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.endDate || '-'}</td>}
                        {/* Conditional data columns based on vehicle type */}
                        {transportData.some(record => record.type !== 'Tipper') && visibleColumns.startreading && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.startreading}</td>
                        )}
                        {transportData.some(record => record.type !== 'Tipper') && visibleColumns.endreading && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.endreading}</td>
                        )}
                        {transportData.some(record => record.type !== 'Tipper') && visibleColumns.totalHours && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm  font-medium">{record.totalHours}</td>
                        )}
                        {visibleColumns.totalReading && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.totalReading || '-'}</td>
                        )}
                        {visibleColumns.shiftType && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.dayNight || '-'}</td>
                        )}
                        {visibleColumns.noOfLoad && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.noOfLoad || '-'}</td>
                        )}
                        {visibleColumns.numberOfDays && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.numberOfDays || record.noofdays || '-'}</td>
                        )}
                        {visibleColumns.grossWeight && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.grossWeight || '-'}</td>}
                        {visibleColumns.tareWeight && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.tareWeight || '-'}</td>}
                        {visibleColumns.netWeight && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.netWeight || '-'}</td>}
                        {transportData.some(record => record.type === 'Tipper') && visibleColumns.loadWt && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.loadWt || '-'}</td>
                        )}
                        {visibleColumns.invoiceNumber && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.invoiceNumber || '-'}</td>
                        )}
                        {visibleColumns.gstNumber && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{record.gstNumber || '-'}</td>
                        )}
                        {visibleColumns.totalAmount && <td className="px-6 py-4 whitespace-nowrap text-sm ">₹{record.totalAmount}</td>}
                        {visibleColumns.gstPercentage && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            {record.gstPercentage != null && record.gstPercentage !== ''
                              ? `${String(record.gstPercentage).replace(/%$/, '').trim()}%`
                              : '-'}
                          </td>
                        )}
                        {visibleColumns.withGST && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm ">
                            {record.withGST ? `₹${record.withGST}` : '-'}
                          </td>
                        )}
                        {visibleColumns.advance && <td className="px-6 py-4 whitespace-nowrap text-sm ">₹{record.advance}</td>}
                        {visibleColumns.balance && <td className="px-6 py-4 whitespace-nowrap text-sm ">₹{record.balance}</td>}
                        {visibleColumns.supervisor && <td className="px-6 py-4 whitespace-nowrap text-sm ">{record.supervisor}</td>}
                        {visibleColumns.actions && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(record.id)}
                                className="text-blue-600 hover:text-blue-900 font-medium"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(record.id)}
                                className="text-red-600 hover:text-red-900 font-medium"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
                </div>
            </div>
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filteredData.length}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </Grid>
        )}

        {/* Add/Edit Transport Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 m-0 overflow-hidden" style={{marginTop: 0}}>
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div 
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={handleClose}
              ></div>

              {/* Modal panel */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all modal-card app-modal-panel">
                <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
                  {/* Header */}
                  <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {isEditMode ? 'Edit Transport' : 'Add Transport'}
                    </h3>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Form content */}
                  <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-4">
                    {/* 1. Location & Date — always visible */}
                    <ModalSection>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            max={getTodayDate()}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Site
                          </label>
                          <select
                            name="site"
                            value={formData.site}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          >
                            <option value="">Select Site Code</option>
                            {siteOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </ModalSection>

                    {/* 2. Reference Details — always visible */}
                    <ModalSection>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                          DC (Delivery Challan) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="dcNumber"
                            value={formData.dcNumber}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No.</label>
                          <input
                            type="text"
                            name="invoiceNumber"
                            value={formData.invoiceNumber || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                          <input
                            type="text"
                            name="gstNumber"
                            value={formData.gstNumber || ''}
                            onChange={handleInputChange}
                            maxLength={15}
                            placeholder=""
                            autoComplete="off"
                            spellCheck={false}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono uppercase"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">GST (%)</label>
                          <input
                            type="text"
                            name="gstPercentage"
                            value={formData.gstPercentage || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                    </ModalSection>

                    {/* Layout for JCB, Poklaine, Bracker */}
                    {(formData.type === 'JCB' || formData.type === 'Poklaine' || formData.type === 'Bracker' || formData.type === 'Bobcat Bucket' || formData.type === 'Bobcat Breaker') && (
                      <>
                        {/* 3. Vehicle Details */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div className='mb-4'>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="type"
                              value={formData.type}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select</option>
                              <option value="JCB">JCB</option>
                              <option value="Poklaine">Poklaine</option>
                              <option value="Tipper">Tipper</option>
                              <option value="Bobcat Bucket">Bobcat Bucket</option>
                                <option value="Bobcat Breaker">Bobcat Breaker</option>
                            </select>
                          </div>
                        </div>

                        {formData.type !== 'JCB' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mt-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                              </label>
                              <input
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleInputChange}
                                max={getTodayDate()}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Time
                              </label>
                              <input
                                type="time"
                                name="startTime"
                                value={formData.startTime}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            <div className='mb-4'>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleInputChange}
                                max={getTodayDate()}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            <div className='mb-4'>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Time
                              </label>
                              <input
                                type="time"
                                name="endTime"
                                value={formData.endTime}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            <div className='mb-4'>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Number of days
                              </label>
                              <input
                                type=""
                                name="numberOfDays"
                                value={formData.numberOfDays}
                                onChange={handleInputChange}
                                onKeyDown={handleNumberInput}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>
                          </div>
                        )}

                        {/* Row 3 for JCB: Hours and Day/Night */}
                        {formData.type === 'JCB' && (
                          <div className="grid grid-cols-4 gap-6 ">
                            <div className='mb-4'>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Hours <span className="text-red-500">*</span>
                              </label>
                              <input
                                type=""
                                name="totalHours"
                                value={formData.totalHours}
                                onChange={handleInputChange}
                                onKeyDown={handleNumberInput}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                            </div>

                            <div className='mb-4'>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Day/Night <span className="text-red-500">*</span>
                              </label>
                              <select
                                name="dayNight"
                                value={formData.dayNight}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              >
                                <option value="">Select</option>
                                <option value="Day">Day</option>
                                <option value="Night">Night</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Row 4: Start Reading, End Reading, Total Reading */}
                        <div className="grid grid-cols-4 gap-6 ">
                        <div className='mb-4'>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Reading
                            </label>
                            <input
                              type=""
                              name="startreading"
                              value={formData.startreading}
                              onChange={handleInputChange}
                              step="0.1"
                              min="0"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder=""
                            />
                          </div>

                          <div className='mb-4'>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Reading
                            </label>
                            <input
                              type=""
                              name="endreading"
                              value={formData.endreading}
                              onChange={handleInputChange}
                              step="0.1"
                              min="0"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder=""
                            />
                          </div>

                          <div className='mb-4'>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Total Reading
                            </label>
                            <input
                              type=""
                              name="totalReading"
                              value={
                                formData.startreading && formData.endreading
                                  ? (parseFloat(formData.endreading) - parseFloat(formData.startreading)).toFixed(1)
                                  : ''
                              }
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                              placeholder=""
                            />
                          </div>
                        </div>
                        </ModalSection>

                        {/* 4. Quantity & Amount */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Total Amount (₹) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="totalAmount"
                              value={formData.totalAmount}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                            <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Advance (₹)
                            </label>
                            <input
                              type="text"
                              name="advance"
                              value={formData.advance}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Balance (₹)
                            </label>
                            <input
                              type="text"
                              name="balance"
                              value={formData.balance || ''}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                          </div>
                        </div>

                        <div className="mt-2 rounded-md bg-green-50 px-4 py-3 text-sm text-teal-800">
                          <span className="mr-1">ⓘ</span>
                          Balance is calculated as: Total Amount + GST Amount – Advance
                        </div>
                        </ModalSection>

                        {/* 5. Additional Details */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle Provider <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="vehicleprovider"
                              value={formData.vehicleprovider}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select</option>
                              <option value="Own">TKD</option>
                              <option value="Rent">Rent</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle #
                            </label>
                            <input
                              type="text"
                              name="vechileNo"
                              value={formData.vechileNo}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Transporter Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="transporter"
                              value={formData.vehicleprovider === 'Rent' ? formData.transporter : 'TKD'}
                              onChange={handleInputChange}
                              readOnly={formData.vehicleprovider !== 'Rent'}
                              disabled={formData.vehicleprovider !== 'Rent'}
                              required
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                formData.vehicleprovider !== 'Rent' ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                              }`}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Supervisor <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="supervisor"
                              value={formData.supervisor}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Supervisor</option>
                              {supervisorsFromApi.length > 0
                                ? supervisorsFromApi.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))
                                : supervisorOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                            </select>
                          </div>
                        </div>
                        </ModalSection>
                      </>
                    )}

                    {/* Layout for Tipper */}
                    {formData.type === 'Tipper' && (
                      <>
                        {/* 3. Vehicle Details */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="type"
                              value={formData.type}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select</option>
                              <option value="JCB">JCB</option>
                              <option value="Poklaine">Poklaine</option>
                              <option value="Tipper">Tipper</option>
                              <option value="Bobcat Bucket">Bobcat Bucket</option>
                              <option value="Bobcat Breaker">Bobcat Breaker</option>
                            </select> 
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              No of Load(s)
                            </label>
                            <input
                              type=""
                              name="noOfLoad"
                              value={formData.noOfLoad || ''}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Number of days
                            </label>
                            <input
                              type=""
                              name="numberOfDays"
                              value={formData.numberOfDays || ''}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Gross Weight
                            </label>
                            <input
                              type=""
                              name="grossWeight"
                              value={formData.grossWeight}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              step="0.01"
                              min="0"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Tare Weight
                            </label>
                            <input
                              type=""
                              name="tareWeight"
                              value={formData.tareWeight}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              step="0.01"
                              min="0"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Net Weight
                            </label>
                            <input
                              type=""
                              name="netWeight"
                              value={formData.netWeight}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                              placeholder=""
                            />
                          </div>
                        </div>
                        </ModalSection>

                        {/* 4. Quantity & Amount */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Total Amount (₹) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="totalAmount"
                              value={formData.totalAmount}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                            <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Advance (₹)
                            </label>
                            <input
                              type="text"
                              name="advance"
                              value={formData.advance}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Balance (₹)
                            </label>
                            <input
                              type="text"
                              name="balance"
                              value={formData.balance || ''}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                          </div>
                        </div>

                        <div className="mt-2 rounded-md bg-green-50 px-4 py-3 text-sm text-teal-800">
                          <span className="mr-1">ⓘ</span>
                          Balance is calculated as: Total Amount + GST Amount – Advance
                        </div>
                        </ModalSection>

                        {/* 5. Additional Details */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle Provider <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="vehicleprovider"
                              value={formData.vehicleprovider}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select</option>
                              <option value="Own">TKD</option>
                              <option value="Rent">Rent</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle #
                            </label>
                            <input
                              type="text"
                              name="vechileNo"
                              value={formData.vechileNo}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Transporter Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="transporter"
                              value={formData.vehicleprovider === 'Rent' ? formData.transporter : 'TKD'}
                              onChange={handleInputChange}
                              readOnly={formData.vehicleprovider !== 'Rent'}
                              disabled={formData.vehicleprovider !== 'Rent'}
                              required
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                formData.vehicleprovider !== 'Rent' ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                              }`}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Supervisor <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="supervisor"
                              value={formData.supervisor}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Supervisor</option>
                              {supervisorsFromApi.length > 0
                                ? supervisorsFromApi.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))
                                : supervisorOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                            </select>
                          </div>
                        </div>
                        </ModalSection>
                      </>
                    )}

                    {/* Default layout when no vehicle type is selected */}
                    {!formData.type && (
                      <>
                        {/* 3. Vehicle Type */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="type"
                              value={formData.type}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select</option>
                              <option value="JCB">JCB</option>
                              <option value="Poklaine">Poklaine</option>
                              <option value="Tipper">Tipper</option>
                              <option value="Bobcat Bucket">Bobcat Bucket</option>
                              <option value="Bobcat Breaker">Bobcat Breaker</option>
                            </select>
                          </div>
                        </div>
                        </ModalSection>

                        {/* 4. Quantity & Amount */}
                        <ModalSection>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Total Amount (₹) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="totalAmount"
                              value={formData.totalAmount}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                            <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Advance (₹)
                            </label>
                            <input
                              type="text"
                              name="advance"
                              value={formData.advance}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Balance (₹)
                            </label>
                            <input
                              type="text"
                              name="balance"
                              value={formData.balance || ''}
                              onChange={handleInputChange}
                              onKeyDown={handleNumberInput}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                          </div>
                        </div>

                        <div className="mt-2 rounded-md bg-green-50 px-4 py-3 text-sm text-teal-800">
                          <span className="mr-1">ⓘ</span>
                          Balance is calculated as: Total Amount + GST Amount – Advance
                        </div>
                        </ModalSection>

                        {/* 5. Additional Details */}
                        <div className="grid grid-cols-4 gap-6 ">
                          <div className=''>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle Provider <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="vehicleprovider"
                              value={formData.vehicleprovider}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select</option>
                            
                              <option value="TKD">TKD</option>
                              <option value="Rent">Rent</option>
                            </select>
                          </div>

                          <div className=''>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Vehicle # <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="vechileNo"
                              value={formData.vechileNo}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>

                          <div className=''>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Transporter Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="transporter"
                              value={formData.vehicleprovider === 'Rent' ? formData.transporter : 'TKD'}
                              onChange={handleInputChange}
                              readOnly={formData.vehicleprovider !== 'Rent'}
                              disabled={formData.vehicleprovider !== 'Rent'}
                              required
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                formData.vehicleprovider !== 'Rent' ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                              }`}
                            />
                          </div>

                          <div className=''>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Supervisor <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="supervisor"
                              value={formData.supervisor}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="">Select Supervisor</option>
                              {supervisorsFromApi.length > 0
                                ? supervisorsFromApi.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))
                                : supervisorOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                            </select>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex flex-shrink-0 justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      style={{ backgroundColor: '#009688' }}
                    >
                      {isEditMode ? 'Update' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Success Popup Modal */}
        {showSuccessPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowSuccessPopup(false)}
            ></div>

            {/* Popup panel */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
              <div className="p-6">
                {/* Success Icon */}
                <div className="flex items-center justify-center w-16 h-16 mx-auto  bg-green-100 rounded-full">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                {/* Message */}
                <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                  Success!
                </h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  {successMessage}
                </p>
                
                {/* Close Button */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setShowSuccessPopup(false)}
                    className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    style={{ backgroundColor: '#009688' }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCancelDelete}
            ></div>

            {/* Modal panel */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
              <div className="p-6">
                {/* Warning Icon */}
                <div className="flex items-center justify-center w-16 h-16 mx-auto  bg-red-100 rounded-full">
                  <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                
                {/* Message */}
                <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                  Delete Transport Record
                </h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  Are you sure you want to delete this transport record? This action cannot be undone.
                </p>
                
                {/* Action Buttons */}
                <div className="flex justify-center space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:opacity-90"
                    style={{ backgroundColor: '#dc2626' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </>
    );
  };

  export default Transport;

